'use client'

import {
  Children,
  Fragment,
  createContext,
  isValidElement,
  useContext,
} from 'react'
import { cn, warnIfEmptyLabel } from '@/utils'

type StepperContextValue = {
  activeStep: number
  completedLabel?: string
  upcomingLabel?: string
}

const StepperContext = createContext<StepperContextValue | null>(null)

const StepperIndexContext = createContext(0)

/**
 * Flattens fragments and nested arrays so a step's position always reflects its
 * visual order, no matter how the consumer groups the children.
 */
const flattenSteps = (children: React.ReactNode): Array<React.ReactNode> =>
  Children.toArray(children).flatMap((child) =>
    isValidElement<{ children?: React.ReactNode }>(child) &&
    child.type === Fragment
      ? flattenSteps(child.props.children)
      : [child],
  )

const useStepperContext = (component: string) => {
  const context = useContext(StepperContext)

  if (!context) {
    throw new Error(`${component} must be rendered inside a <Stepper>.`)
  }

  return context
}

export type StepperProps = {
  /**
   * Zero-based index of the step the user is currently on. Every step up to and
   * including it is rendered as reached; the remaining ones as upcoming. Values
   * outside the range are clamped.
   */
  activeStep: number
  /**
   * Accessible name for the step list (e.g. "Progresso da compra"). Required —
   * provide a localized string from the consuming app.
   */
  label: string
  /**
   * The `StepperItem` elements to render, in order. The step count is however
   * many are rendered.
   */
  children: React.ReactNode
  /**
   * Visually hidden suffix announced after the label of a step the user has
   * already completed (e.g. "concluído"). Optional — omit it and assistive
   * technologies only hear which step is current. Provide a localized string.
   */
  completedLabel?: string
  /**
   * Visually hidden suffix announced after the label of a step the user has not
   * reached yet (e.g. "não iniciado"). Optional. Provide a localized string.
   */
  upcomingLabel?: string
} & Omit<React.ComponentPropsWithoutRef<'ol'>, 'children' | 'aria-label'>

/**
 * Horizontal progress indicator for a linear, multi-step flow. Each step owns a
 * segment of the progress bar plus an icon and a label below it.
 *
 * Below the `lg` breakpoint the row fills its container so it follows the page
 * grid. From `lg` up it takes its fixed design width and centres itself, so it
 * looks the same on every desktop size. A label that no longer fits its column
 * is truncated, so the row never overflows.
 */
export const Stepper = ({
  activeStep,
  label,
  children,
  completedLabel,
  upcomingLabel,
  className,
  ...props
}: StepperProps) => {
  warnIfEmptyLabel(label, 'Stepper: label')

  if (completedLabel !== undefined) {
    warnIfEmptyLabel(completedLabel, 'Stepper: completedLabel')
  }

  if (upcomingLabel !== undefined) {
    warnIfEmptyLabel(upcomingLabel, 'Stepper: upcomingLabel')
  }

  const items = flattenSteps(children)
  const lastIndex = items.length - 1
  const currentStep = Math.min(Math.max(activeStep, 0), Math.max(lastIndex, 0))

  return (
    <StepperContext.Provider
      value={{ activeStep: currentStep, completedLabel, upcomingLabel }}
    >
      {/*
        The 661px cap is the fixed desktop width from the design. Combined with
        the 200px per-step cap it also produces the designed 30.5px gaps, since
        `justify-between` spreads the 61px of remaining space.
      */}
      <ol
        role="list"
        aria-label={label}
        className={cn(
          'gap-nano flex items-center justify-between lg:mx-auto lg:max-w-[661px]',
          className,
        )}
        {...props}
      >
        {items.map((item, index) => (
          <StepperIndexContext.Provider
            // `Children.toArray` assigns a stable key to every element, which
            // survives a consumer reordering the steps. Index is only a
            // fallback for the non-element children it lets through.
            key={isValidElement(item) && item.key != null ? item.key : index}
            value={index}
          >
            {item}
          </StepperIndexContext.Provider>
        ))}
      </ol>
    </StepperContext.Provider>
  )
}

export type StepperItemProps = {
  /**
   * Decorative icon rendered before the label. Sized and coloured by the
   * component, so pass the raw icon (e.g. `<Bag />`) without extra classes.
   */
  icon?: React.ReactNode
  children: React.ReactNode
} & Omit<React.ComponentPropsWithoutRef<'li'>, 'children'>

/**
 * A single step inside a `Stepper`. Its position is derived from the render
 * order, so steps must be children of `Stepper`.
 */
export const StepperItem = ({
  icon,
  children,
  className,
  ...props
}: StepperItemProps) => {
  const { activeStep, completedLabel, upcomingLabel } =
    useStepperContext('StepperItem')
  const index = useContext(StepperIndexContext)

  const isCurrent = index === activeStep
  const isReached = index <= activeStep
  const state = isCurrent ? 'current' : isReached ? 'complete' : 'upcoming'

  // `aria-current` already announces the current step, so only the other two
  // states need spelling out for assistive technologies.
  const stateLabel = isCurrent
    ? undefined
    : isReached
      ? completedLabel
      : upcomingLabel

  return (
    <li
      aria-current={isCurrent ? 'step' : undefined}
      data-state={state}
      className={cn(
        'gap-3xs lg:max-w-50 group flex min-w-0 flex-1 flex-col items-start lg:items-center',
        'text-support-200 data-[state=upcoming]:text-gray-500',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className="bg-support-100 h-nano w-full rounded-full group-data-[state=upcoming]:bg-gray-200 lg:h-3"
      />

      <span className="gap-nano lg:px-nano flex w-full min-w-0 items-center justify-start lg:justify-center">
        {icon && (
          <span
            aria-hidden="true"
            className="bg-support-100 size-2xs lg:size-xs flex shrink-0 items-center justify-center rounded-full group-data-[state=upcoming]:bg-gray-200 [&_svg]:size-3.5 lg:[&_svg]:size-5"
          >
            {icon}
          </span>
        )}

        <span className="text-2xs leading-distant min-w-0 truncate font-medium">
          {children}
        </span>

        {stateLabel && <span className="sr-only">{` ${stateLabel}`}</span>}
      </span>
    </li>
  )
}
